/**
 * @architecture-header 1
 * @layer app
 * @domain authentication
 * @responsibility Resolve host Supabase Auth or FastAPI organization-cookie sessions into one in-memory identity projection.
 * @state-owner host auth adapter or FastAPI cookie session; React context is an in-memory projection
 * @allowed-imports React; app environment
 * @forbidden localStorage; Zustand; role or permission ownership; API entity cache
 * @public-api app providers and authenticated routes
 * @side-effects session bootstrap, organization logout and host auth-state subscription with cleanup
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 * @security-boundary host authentication adapter to shared React product
 * @data-classification user identity in memory; access token remains host-private
 * @trust-model host session proves identity only; backend remains authorization authority
 */

import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useWorkspaceEnvironment,
  type WorkspaceAuthAdapter,
  type WorkspaceSession,
} from '../config/environment';

interface SessionContextValue {
  acceptOrganizationSession(session: OrganizationSessionIdentity): void;
  auth: WorkspaceAuthAdapter;
  error: Error | null;
  isLoading: boolean;
  session: WorkspaceSession | null;
  refresh(): Promise<void>;
  signOut(): Promise<void>;
}

export interface OrganizationSessionIdentity {
  csrf_token: string;
  expires_at: string;
  status: 'authenticated';
  user_id: string;
}

type SessionSource = 'host' | 'organization' | null;

function projectOrganizationSession(session: OrganizationSessionIdentity): WorkspaceSession {
  return { displayName: null, email: '', userId: session.user_id };
}

function isMissingOrganizationSession(cause: unknown): boolean {
  return Boolean(cause && typeof cause === 'object' && 'status' in cause && cause.status === 401);
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: PropsWithChildren) {
  const { apiClient, auth } = useWorkspaceEnvironment();
  const [session, setSession] = useState<WorkspaceSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const sourceRef = useRef<SessionSource>(null);
  const organizationCsrfRef = useRef<string | null>(null);

  const applyHostSession = useCallback((nextSession: WorkspaceSession | null) => {
    if (nextSession) {
      sourceRef.current = 'host';
      organizationCsrfRef.current = null;
      setSession(nextSession);
      return;
    }
    if (sourceRef.current !== 'organization') {
      sourceRef.current = null;
      setSession(null);
    }
  }, []);

  const acceptOrganizationSession = useCallback((nextSession: OrganizationSessionIdentity) => {
    sourceRef.current = 'organization';
    organizationCsrfRef.current = nextSession.csrf_token;
    setError(null);
    setIsLoading(false);
    setSession(projectOrganizationSession(nextSession));
  }, []);

  const resolveSession = useCallback(async () => {
    const hostSession = await auth.getSession();
    if (hostSession) {
      applyHostSession(hostSession);
      return;
    }
    try {
      const organization = await apiClient.request<OrganizationSessionIdentity>(
        '/api/v1/auth/org-session',
      );
      acceptOrganizationSession(organization);
    } catch (cause) {
      if (!isMissingOrganizationSession(cause)) throw cause;
      sourceRef.current = null;
      organizationCsrfRef.current = null;
      setSession(null);
    }
  }, [acceptOrganizationSession, apiClient, applyHostSession, auth]);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      await resolveSession();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error('Unable to load the session'));
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, [resolveSession]);

  const signOut = useCallback(async () => {
    if (sourceRef.current === 'organization') {
      const csrfToken = organizationCsrfRef.current;
      if (!csrfToken) throw new Error('Organization session is missing its CSRF proof');
      await apiClient.request('/api/v1/auth/org-logout', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
      });
      sourceRef.current = null;
      organizationCsrfRef.current = null;
      setSession(null);
      return;
    }
    await auth.signOut();
    applyHostSession(null);
  }, [apiClient, applyHostSession, auth]);

  useEffect(() => {
    let active = true;
    void resolveSession()
      .catch((cause: unknown) => {
        if (active) {
          setError(cause instanceof Error ? cause : new Error('Unable to load the session'));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    const unsubscribe = auth.subscribe((nextSession) => {
      if (!active) return;
      applyHostSession(nextSession);
      setError(null);
      setIsLoading(false);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyHostSession, auth, resolveSession]);

  const value = useMemo(
    () => ({ acceptOrganizationSession, auth, error, isLoading, refresh, session, signOut }),
    [acceptOrganizationSession, auth, error, isLoading, refresh, session, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useWorkspaceSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('SessionProvider is missing');
  return value;
}
